/**
 * Card Comment Moderation Service (story 17374)
 *
 * API client for the card-thread comment moderation endpoints. 17329 shipped
 * these server-side, built to MIRROR the Journals reported-comment contract
 * consumed by adminModerationService.ts — same Pending/Resolved statuses,
 * same NoAction/Hidden/Deleted outcomes, same `{ items, totalCount }`
 * envelope. This is a second fetch against a sibling contract, not a
 * translation layer, so it is a separate file rather than a rewrite of
 * adminModerationService.ts (leave the Journals path untouched).
 *
 * Base-URL convention — mirrored from adminModerationService.ts's routing
 * note: that file documents a live incident where a rewrite made every
 * moderation call 404 because it assumed a host it didn't have. The lesson
 * there was per-service routes must resolve against the host that actually
 * serves them, never a hardcoded one. Card comments live on TaskManager.API,
 * which is the SAME host the admin SWA's VITE_API_URL already points at (see
 * that file's comment) — so, like adminReportsService.ts's other
 * TaskManager-hosted routes, a plain relative `/api/...` path is correct: it
 * resolves through authenticatedFetch's own VITE_API_URL prefixing
 * (src/api/authenticatedFetch.ts) in deployed builds and through the Vite
 * `/api` proxy (vite.config.ts) in local dev. No extra per-service base URL
 * constant is needed here — adding one pointed at the wrong host is exactly
 * the mistake that caused the 404 incident being mirrored.
 *
 * Endpoints (all admin-only, require Admin role in JWT):
 *   GET  /api/TaskManager/Card/comments/reported                    — paginated reported list
 *   POST /api/TaskManager/Card/comments/reported/{reportId}/resolve — resolve with outcome
 *   POST /api/TaskManager/Card/comments/{cardActivityId}/hide       — hide a comment
 *   POST /api/TaskManager/Card/comments/{cardActivityId}/unhide     — unhide a comment
 *
 * Resolving with outcome `Hidden` hides the comment server-side in the same
 * call — callers must NOT also call hideCardComment for that report, or the
 * comment gets acted on twice for one decision.
 */

import { authenticatedFetch } from '../api/authenticatedFetch';
import type { ReportOutcome, ReportStatus, CardCommentReportRow } from '../types/comment';

const CARD_COMMENTS = '/api/TaskManager/Card/comments';

// ---------------------------------------------------------------------------
// Internal helper — mirrors adminModerationService.ts's callApi shape
// ---------------------------------------------------------------------------

interface ApiResult<T> {
  data: T;
  success: boolean;
  error?: string;
}

async function callApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResult<T>> {
  try {
    const response = await authenticatedFetch(endpoint, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers ?? {}),
      },
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({})) as Record<string, unknown>;
      return {
        data: null as T,
        success: false,
        error: (body['message'] as string | undefined) ?? `HTTP ${response.status}: ${response.statusText}`,
      };
    }

    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return { data: null as T, success: true };
    }

    const data = await response.json() as T;
    return { data, success: true };
  } catch (err) {
    return {
      data: null as T,
      success: false,
      error: err instanceof Error ? err.message : 'Network error',
    };
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Retrieve paginated reported card comments.
 * GET /api/TaskManager/Card/comments/reported?status=&pageIndex=&pageSize=
 */
export async function getReportedCardComments(opts: {
  status?: ReportStatus;
  pageIndex?: number;
  pageSize?: number;
}): Promise<{ items: CardCommentReportRow[]; totalCount: number }> {
  const params = new URLSearchParams();
  if (opts.status !== undefined) params.set('status', opts.status);
  if (opts.pageIndex !== undefined) params.set('pageIndex', String(opts.pageIndex));
  if (opts.pageSize !== undefined) params.set('pageSize', String(opts.pageSize));

  const qs = params.toString();
  const url = `${CARD_COMMENTS}/reported${qs ? `?${qs}` : ''}`;

  const result = await callApi<{ items: CardCommentReportRow[]; totalCount: number }>(url);
  if (!result.success || !result.data) {
    throw new Error(result.error ?? 'Failed to fetch reported card comments');
  }
  return { items: result.data.items ?? [], totalCount: result.data.totalCount ?? 0 };
}

/**
 * Resolve a card-comment report with an outcome decision.
 * POST /api/TaskManager/Card/comments/reported/{reportId}/resolve
 * body: { Outcome, CardActivityId }
 *
 * Outcome `Hidden` hides the comment server-side as part of this same call —
 * do not follow this with hideCardComment(cardActivityId).
 */
export async function resolveCardCommentReport(
  reportId: string,
  cardActivityId: number,
  outcome: ReportOutcome
): Promise<void> {
  const result = await callApi<void>(`${CARD_COMMENTS}/reported/${reportId}/resolve`, {
    method: 'POST',
    body: JSON.stringify({ Outcome: outcome, CardActivityId: cardActivityId }),
  });
  if (!result.success) {
    throw new Error(result.error ?? 'Failed to resolve card comment report');
  }
}

/**
 * Hide a card comment (admin only, reversible).
 * POST /api/TaskManager/Card/comments/{cardActivityId}/hide
 */
export async function hideCardComment(cardActivityId: number): Promise<void> {
  const result = await callApi<void>(`${CARD_COMMENTS}/${cardActivityId}/hide`, { method: 'POST' });
  if (!result.success) {
    throw new Error(result.error ?? 'Failed to hide card comment');
  }
}

/**
 * Unhide a previously hidden card comment (admin only).
 * POST /api/TaskManager/Card/comments/{cardActivityId}/unhide
 */
export async function unhideCardComment(cardActivityId: number): Promise<void> {
  const result = await callApi<void>(`${CARD_COMMENTS}/${cardActivityId}/unhide`, { method: 'POST' });
  if (!result.success) {
    throw new Error(result.error ?? 'Failed to unhide card comment');
  }
}

export const cardCommentModerationService = {
  getReportedCardComments,
  resolveCardCommentReport,
  hideCardComment,
  unhideCardComment,
};
